<script>
import { mapStores } from "pinia";
import { ErrorMessage, Field, Form as VForm } from "vee-validate";
import { defineComponent } from "vue";
import { RouterLink } from "vue-router";

import TgButton from "@/abc-dsk/components/Button/TgButton.vue";
import TgFileInput from "@/abc-dsk/components/Form/TgFileInput.vue";
import { isValidationFailedResponse } from "@/common/api/api.service";
import SupportService, { ATTACHMENT_ALLOWED_EXT } from "@/common/api/support.service";
import { translateFromLaravel } from "@/common/vee-validate-helpers";
import { useLanguageLevelStore, useSettingsStore } from "@/stores";

/**
 * @readonly
 * @enum {string}
 */
const FormStateEnum = {
  Idle: "idle",
  Submitting: "submitting",
  Submitted: "submitted",
  Failed: "failed"
};

export default defineComponent({
  name: "SupportForm",
  components: {
    ErrorMessage,
    Field,
    VForm,
    TgButton,
    TgFileInput,
    RouterLink
  },
  emits: ["submitted"],
  data() {
    return {
      formState: FormStateEnum.Idle
    };
  },
  computed: {
    ...mapStores(useLanguageLevelStore, useSettingsStore),

    languageLevel() {
      return this["language-levelStore"].activeLanguageLevel;
    },

    packageGroup() {
      return this.settingsStore.preferredPackageGroup;
    },

    isSubmitting() {
      return this.formState === FormStateEnum.Submitting;
    },
    isSubmitted() {
      return this.formState === FormStateEnum.Submitted;
    },
    hasFailed() {
      return this.formState === FormStateEnum.Failed;
    },
    fileValidationRules() {
      return "max_array:5|ext:" + ATTACHMENT_ALLOWED_EXT.join(",");
    }
  },
  methods: {
    setValidationErrors(externalValidationErrors) {
      const validationErrors = Object.entries(externalValidationErrors).reduce(
        (errors, [field, value]) => {
          const mappedField = field.startsWith("attachments.") ? "attachments" : field;
          return {
            ...errors,
            [mappedField]: value
          };
        },
        {}
      );

      this.$refs.vForm.setErrors(translateFromLaravel(validationErrors));
    },
    getFormData(values) {
      const formData = new FormData();

      for (const key in values) {
        if (key === "attachments") {
          values[key].forEach((file) => formData.append("attachments[]", file));
        } else {
          formData.append(key, values[key]);
        }
      }

      formData.append("activeLanguageLevel", this.languageLevel);
      formData.append("activeLanguage", this.settingsStore.activeLanguageGroup);
      formData.append("activePackage", this.packageGroup.id);

      return formData;
    },
    async onSubmit(values) {
      if (this.isSubmitting) {
        return;
      }

      try {
        this.formState = FormStateEnum.Submitting;

        const formData = this.getFormData(values);
        await SupportService.submit(formData);

        this.formState = FormStateEnum.Submitted;
        this.$refs.vForm.resetForm();
        this.$refs.fileInput.reset();

        this.$emit("submitted");
      } catch (error) {
        if (isValidationFailedResponse(error.response)) {
          this.formState = FormStateEnum.Idle;

          this.setValidationErrors(error.response.data.data);
        } else {
          this.formState = FormStateEnum.Failed;
        }
      }
    }
  }
});
</script>

<template>
  <VForm v-slot="{ errors }" ref="vForm" class="support-form" @submit="onSubmit($event)">
    <fieldset :disabled="isSubmitting">
      <div class="mb-2">
        <Field
          name="subject"
          rules="required|max:200"
          :placeholder="$t('supportForm.inputPlaceholders.subject')"
          class="support-form__input"
          :class="{ 'support-form__input--error': errors?.subject?.length > 0 }"
        />
        <ErrorMessage name="subject" class="support-form__error-message" />
      </div>

      <div class="mb-2">
        <Field
          as="textarea"
          value=""
          name="message"
          type="textarea"
          rules="max:2000"
          rows="8"
          :placeholder="$t('supportForm.inputPlaceholders.message')"
          class="support-form__input"
          :class="{ 'support-form__input--error': errors?.message?.length > 0 }"
        />
        <ErrorMessage name="message" class="support-form__error-message" />
      </div>

      <div class="mb-4">
        <Field
          name="email"
          type="email"
          rules="required|email"
          rows="8"
          autocomplete="email"
          :placeholder="$t('supportForm.inputPlaceholders.email')"
          class="support-form__input"
          :class="{ 'support-form__input--error': errors?.email?.length > 0 }"
        />
        <ErrorMessage name="email" class="support-form__error-message" />
      </div>

      <label for="support-form-file-upload-input" class="support-form__submission-label mb-1">
        {{ $t("supportForm.attachFile") }}
      </label>
      <div>
        <Field
          v-slot="{ handleChange }"
          name="attachments"
          :rules="fileValidationRules"
          :value="[]"
        >
          <TgFileInput
            ref="fileInput"
            input-id="support-form-file-upload-input"
            :has-error="errors?.attachments?.length > 0"
            :browse-files-text="$t('file-input.browse-files')"
            :drop-files-here-text="$t('file-input.drop-files-here')"
            @input="handleChange($event)"
          />
        </Field>
        <ErrorMessage name="attachments" class="support-form__error-message" />
      </div>

      <TgButton type="submit" class="mt-4" :disabled="isSubmitting" full-width>
        {{ isSubmitting ? $t("supportForm.submitting") : $t("supportForm.submit") }}
      </TgButton>

      <i18n-t
        keypath="supportModal.privacyDisclaimer"
        scope="global"
        tag="p"
        class="support-form__privacy-disclaimer mt-4 mb-0"
      >
        <template #privacyPolicy>
          <RouterLink :to="{ name: 'policies', params: { policy: 'privacy' } }" target="_blank">
            {{ $t("supportModal.privacyPolicy") }}
          </RouterLink>
        </template>
        <template #cookiePolicy>
          <RouterLink :to="{ name: 'policies', params: { policy: 'cookie' } }" target="_blank">
            {{ $t("supportModal.cookiePolicy") }}
          </RouterLink>
        </template>
      </i18n-t>

      <div v-if="hasFailed" class="support-form__error-message">
        {{ $t("supportForm.submitFailed") }}
      </div>
    </fieldset>
  </VForm>
</template>
